// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import {
  MatDialog, MatDialogRef,
  MAT_DIALOG_DATA
} from "@angular/material/dialog";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { distinctUntilChanged, finalize, startWith, take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CommunicationsService } from "src/app/modules/communications/communications.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { ChooseRecipientDialogComponent } from "src/app/shared/components/dialogs/choose-recipient-dialog/choose-recipient-dialog.component";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { DocumentArchivesService } from "../../document-archives.service";

@Component({
  selector: "app-cancel-contract-dialog",
  templateUrl: "./cancel-contract-dialog.component.html",
  styleUrls: ["./cancel-contract-dialog.component.scss"],
})
export class CancelContractDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  customContactField: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  showBcc = false;
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("properties.dialog.contractCancellationContinue"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-paper-plane",
      inline: true,
    },
  };
  attachmentBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.attach"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "accent",
    spinnerColor: "accent",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-paperclip",
      inline: true,
    },
  };
  // END - Add communication form

  dialogIsLoading = true;

  isAdmin = false;
  isMaster = false;
  phonebook = [];
  companyId: string = "";

  step1 = true;
  docs: any[] = [];
  selectedDocs: any[] = [];
  barCode = "";

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<CancelContractDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public confirmDialog: MatDialog,
    public chooseRecipientsDialog: MatDialog,
    public configService: ConfigService,

    private cookieService: CookieService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private communicationsService: CommunicationsService,
    private companiesService: CompaniesService,
    private authService: AuthService,
    private helperService: HelperService,
    private documentArchivesService: DocumentArchivesService,
    private apiService: ApiService
  ) {}

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) =>
        this.loggedUser.setData(res)
      )
    );
    if (
      this.authService.loggedUserRequestedRoleType$.getValue() === "admin" ||
      this.authService.loggedUserRequestedRoleType$.getValue() === "operator"
    ) {
      this.isAdmin = true;
    }
    if (
      this.authService.loggedUserRequestedRoleType$.getValue() === "master" ||
      this.authService.loggedUserRequestedRoleType$.getValue() ===
        "collaborator"
    ) {
      this.isMaster = true;
    }

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    this.documentArchivesService
      .getDocsByBarcode(this.data.contract.documentArchiveId, this.data.contract.barCode)
      .subscribe((res) => {
        if (res.status) {
          if (res.data && Array.isArray(res.data)) {
            res.data.forEach((item: any) => {
              this.docs.push({
                _id: item._id,
                docTitle: item.docTitle,
                structureObjectName: item.structureObjectName,
                structureObjectId: item.structureObjectId,
                data: item.data,
                checked: false
              })
            });
          }
        } else {
          this.toastrService.error(res.message);
        }
        this.dialogIsLoading = false;
      });

    this.barCode = this.data.contract.barCode.substring(this.data.contract.barCode.length - 7);

    // Set company
    this.companyId = this.cookieService.get("requestedCompany");
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Update docs array with incoming element
   *
   * @since 1.0.0
   */
  onDocSelected(doc: any, checked: boolean): void {
    if (doc && checked) {
      this.selectedDocs.push(doc);
    } else if (doc && !checked) {
      const index = this.selectedDocs.findIndex((el: any) => el._id === doc._id);
      if (index > -1) {
        this.selectedDocs.splice(index, 1);
      }
    }
  }

  /**
   * Handle step 2, hide docs selection and show email form
   *
   * @since 1.0.0
   */
  onStep2(): void {
    this.step1 = false;
    this.dialogIsLoading = true;
    // Get form initial values and init the form
    this.getDataAndInitForm();
  }

  /**
   * Preload data and then init form. If something
   * goes wrong just init the form
   *
   * @since 1.0.0
   */
  getDataAndInitForm(): void {

    // Normalize each file inside doc
    if (!this.model.docs) {
      this.model = {
        ...this.model,
        docs: [],
      };
    }
    for (const doc of this.selectedDocs) {
      if (
        doc && 
        (!Array.isArray(doc.data.document) || doc.data.document.length === 0)
      ) {
        return;
      }

      for (const document of doc.data.document) {
        let newDoc = {};
        newDoc = {
          key: document._id,
          value: `${doc.docTitle} ${doc.structureObjectName} - ${document.originalName}`,
          path: document.path,
        };
        this.model.docs.push(newDoc);
      }
    }

    // Get available recipients from phonebook
    if (this.isAdmin) {
      this.subscriptions.push(
        this.companiesService
          .getPhonebookListAdmin(0, null, null, "asc")
          .subscribe(
            (res) => {
              if (res.status) {
                this.phonebook = res.data.items;
                this.initForm();
              }
              this.dialogIsLoading = false;
            },
            (err) => {
              this.dialogIsLoading = false;
              this.helperService.handleError(err);
            }
          )
      );
    } else if (this.isMaster) {
      this.subscriptions.push(
        this.companiesService
          .getPhonebookList(this.companyId, 0, null, null, "asc")
          .subscribe(
            (res) => {
              if (res.status) {
                this.phonebook = res.data.items;
                this.initForm();
              }
              this.dialogIsLoading = false;
            },
            (err) => {
              this.dialogIsLoading = false;
              this.helperService.handleError(err);
            }
          )
      );
    }
  }

  /**
   * Autofill body on cancellationType changes
   *
   * @since 1.0.0
   */
  updateCancellationType(): void {
    const cancellationTypeControl = this.form.controls.cancellationType;
    const bodyControl = this.form.controls.body;

    if (cancellationTypeControl) {
      cancellationTypeControl.valueChanges
      .pipe(distinctUntilChanged(), startWith(cancellationTypeControl.value))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          }
          let titles = '';
          for (const doc of this.selectedDocs) {
            titles = `${titles}- ${doc.docTitle} ${doc.structureObjectName}\n`;
          }
          if (res === 'expiration') {
            bodyControl.setValue(this.translateService.instant("communications.fields.cancelContractBody", { titles: titles }));
          } else if (res === 'immediate') {
            bodyControl.setValue(this.translateService.instant("communications.fields.cancelContractImmediateBody", { titles: titles }));
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Minimum char amount to activate search filtering
    const minTermLength = 1;
    // Set fields
    this.fields = [
      {
        fieldGroupClassName: "row",
        hooks: {
          onInit: (fieldConfig) => {
            this.updateCancellationType();
          },
        },
        fieldGroup: [
          {
            className: "col-12 mt-3 radio-wrapper",
            type: "radio",
            key: "cancellationType",
            defaultValue: "immediate",
            templateOptions: {
              label: this.translateService.instant(
                "communications.fields.cancelContractType"
              ),
              options: [
                {
                  value: "immediate",
                  label: this.translateService.instant(
                    "communications.fields.cancelContractImmediate"
                  ),
                },
                {
                  value: "expiration",
                  label: this.translateService.instant(
                    "communications.fields.cancelContractExpiration"
                  ),
                },
              ],
            },
          },
          {
            key: "sender",
            hide: true,
            templateOptions: {
              value: this.loggedUser.get("email"),
            },
          },
          {
            className: "col-11 mb-0 autocomplete-multi-email",
            type: "autocomplete",
            key: "recipients",
            templateOptions: {
              customLabel: this.translateService.instant(
                "communications.fields.recipients"
              ),
              required: true,
              bindLabel: "email",
              bindValue: "email",
              multiple: true,
              search$: this.isMaster
                ? this.companiesService.getPhonebookListMaster.bind(this)
                : this.companiesService.getPhonebookListAdmin.bind(this),
              hideSelected: true,
              addTag: true,
              minTermLength,
              typeToSearchText: this.translateService.instant(
                "common.words.typeToSearch",
                { minTermLength }
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              addTagText: this.translateService.instant("common.words.addTag"),
              loadingText: this.translateService.instant(
                "common.words.loading"
              ),
            },
            expressionProperties: {
              "templateOptions.onChange": (model) => {
                return model.recipients;
              },
            },
          },
          {
            className: "col-1 mb-0",
            type: "button",
            key: "openPhonebook",
            templateOptions: {
              color: "accent",
              tooltip: "tooltip",
              icon: {
                fontSet: "fas",
                fontIcon: "fa-book-user",
              },
              onClick: ($event) =>
                this.onChooseRecipientDialogOpen("recipients"),
            },
          },
          {
            className: "col-11 mb-0 autocomplete-multi-email",
            type: "autocomplete",
            key: "recipientsCc",
            // hideExpression: (model): boolean => !model.showCc,
            templateOptions: {
              customLabel: this.translateService.instant(
                "communications.fields.recipientsCc"
              ),
              required: false,
              bindLabel: "email",
              bindValue: "email",
              multiple: true,
              search$: this.isMaster
                ? this.companiesService.getPhonebookListMaster.bind(this)
                : this.companiesService.getPhonebookListAdmin.bind(this),
              hideSelected: true,
              addTag: true,
              minTermLength,
              typeToSearchText: this.translateService.instant(
                "common.words.typeToSearch",
                { minTermLength }
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              addTagText: this.translateService.instant("common.words.addTag"),
              loadingText: this.translateService.instant(
                "common.words.loading"
              ),
            },
            expressionProperties: {
              "templateOptions.onChange": (model) => {
                return model.recipientsCc;
              },
            },
          },
          {
            className: "col-1 mb-0",
            type: "button",
            key: "openPhonebook",
            templateOptions: {
              color: "accent",
              tooltip: "tooltip",
              icon: {
                fontSet: "fas",
                fontIcon: "fa-book-user",
              },
              onClick: ($event) =>
                this.onChooseRecipientDialogOpen("recipientsCc"),
            },
          },
          {
            className: "col-11 mb-0 autocomplete-multi-email",
            type: "autocomplete",
            key: "recipientsBcc",
            // hideExpression: (model): boolean => !model.showBcc,
            templateOptions: {
              customLabel: this.translateService.instant(
                "communications.fields.recipientsBcc"
              ),
              required: false,
              bindLabel: "email",
              bindValue: "email",
              multiple: true,
              search$: this.isMaster
                ? this.companiesService.getPhonebookListMaster.bind(this)
                : this.companiesService.getPhonebookListAdmin.bind(this),
              hideSelected: true,
              addTag: true,
              minTermLength,
              typeToSearchText: this.translateService.instant(
                "common.words.typeToSearch",
                { minTermLength }
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              addTagText: this.translateService.instant("common.words.addTag"),
              loadingText: this.translateService.instant(
                "common.words.loading"
              ),
            },
            expressionProperties: {
              "templateOptions.onChange": (model) => {
                return model.recipientsBcc;
              },
            },
          },
          {
            className: "col-1 mb-0",
            type: "button",
            key: "openPhonebook",
            templateOptions: {
              color: "accent",
              tooltip: "tooltip",
              icon: {
                fontSet: "fas",
                fontIcon: "fa-book-user",
              },
              onClick: ($event) =>
                this.onChooseRecipientDialogOpen("recipientsBcc"),
            },
          },
          {
            className: "col-12 mb-0",
            template: "<hr>",
          },
          {
            className: "col-12 pt-2 mb-1",
            type: "input",
            key: "subject",
            defaultValue: this.data.subject ? this.data.subject : "",
            templateOptions: {
              label: this.translateService.instant(
                "communications.fields.subject"
              ),
              placeholder: this.translateService.instant(
                "communications.fields.subject"
              ),
              required: false,
              minLength: 1,
              maxLength: 200,
            },
          },
          {
            className: "col-12",
            type: "textarea",
            key: "body",
            defaultValue: this.data.body ? this.data.body : "",
            templateOptions: {
              label: this.translateService.instant(
                "communications.fields.body"
              ),
              placeholder: this.translateService.instant(
                "communications.fields.body"
              ),
              required: true,
              maxLength: 1000,
              rows: 6,
            },
          },
          {
            className: "col-12 col-md-6 mb-0",
            type: "file-uploader",
            key: "attachments",
            templateOptions: {
              required: false,
              allowedFilesNumber: 3,
              customLabel: this.translateService.instant(
                "communications.fields.attachments"
              ),
            },
          },
        ],
      },
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any, created?: boolean): void {
    // Prevent unsaved data loss
    if (!created && this.form.dirty && !this.form.pristine) {
      const confirmDialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
        minWidth: "50%",
        maxHeight: "98vh",
        data: {
          title: this.translateService.instant("common.warnings.warning"),
          message: this.translateService.instant("common.warnings.unsavedData"),
          btnOkText: this.translateService.instant("common.cta.continue"),
          btnCancelText: this.translateService.instant("common.cta.undo"),
          // btnCancelIcon: 'fa-times-circle',
        },
        disableClose: true,
      });
      // Subscribe to dialog result (only for 1 emit thanks to "take()")
      confirmDialogRef
        .beforeClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (!res) {
            return;
          }
          this.dialogRef.close(data);
          return;
        });
    } else {
      this.dialogRef.close(data);
    }
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // One message at a time: «Prosegui» is off from the click to the answer of the back-end, so a
    // second click can neither open a second confirmation nor send the cancellation twice
    if (this.formBtnOptions.active || this.formBtnOptions.disabled) {
      return;
    }

    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }
    this.formBtnOptions.disabled = true;

    // Set recipients in model
    // this.model = {
    //   ...this.model,
    //   recipients: this.selectedRecipients.map((obj: any) => obj.email),
    // };

    let confirmDialogRef;

    // Email subject is not mandatory, but if it's empty we open a confirm dialog
    // to inform user that there's no subject in mail
    if (!this.form.controls["subject"].value) {
      confirmDialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
        minWidth: "50%",
        maxHeight: "98vh",
        data: {
          title: this.translateService.instant("dialogs.confirm.title"),
          message: this.translateService.instant(
            "communications.add.noSubjectDialog.message"
          ),
          btnOkText: this.translateService.instant(
            "communications.add.noSubjectDialog.confirm"
          ),
          btnOkIcon: "fa-paper-plane",
          btnCancelText: this.translateService.instant("common.cta.undo"),
          btnCancelIcon: "fa-times-circle",
        },
      });
    } else {
      // Open confirm dialog before sending email
      confirmDialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
        minWidth: "50%",
        maxHeight: "98vh",
        data: {
          title: this.translateService.instant("dialogs.confirm.title"),
          message: this.translateService.instant(
            "communications.add.confirmMessage"
          ),
          btnOkText: this.translateService.instant("common.cta.send"),
          btnOkIcon: "fa-paper-plane",
          btnCancelText: this.translateService.instant("common.cta.undo"),
          btnCancelIcon: "fa-times-circle",
        },
      });
    }

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    confirmDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          // Back to main dialog
          this.formBtnOptions.disabled = false;
          return;
        }
        // User confirmed, can send mails
        this.dialogIsLoading = true;
        this.formBtnOptions.active = true;
        this.createCommunication();
      });
  }

  /**
   * Create a new communication and send it
   *
   * @since 1.0.0
   */
  createCommunication() {
    // If message has no subject, let's put a default one before sending
    if (this.model && !this.model.subject) {
      this.model.subject = this.translateService.instant(
        "communications.add.defaultSubject"
      );
    }

    // Handle body formatting. Built on a copy: the text in the dialog stays as typed, so a retry
    // after a failure sends the list once
    let body = this.model.body.concat(`<ul>`);
    this.model.docs.forEach((doc: any) => {
      if (!doc.path) {
        // This would never happen
        this.toastrService.error("common.errors.unknown");
        return;
      }
      const name = doc.value;
      const path = doc.path.replace(/src[\\\/]assets/g, 'public');
      let url = "";
      if (path.substring(0, 22) === "https://app.archedi.it") {
        // url = path;
        // `docFilename` keeps its leading slash, so no slash is added after "documents"
        const docFilename = path.substr(path.lastIndexOf("/"));
        url = `${this.configService.apiHost}/uploads/properties/${this.data.contract.propertyId}/documents${docFilename}`;
      } else if (/^https?:\/\//i.test(path)) {
        // Already a complete address: prefixing the API host would double it
        url = path;
      } else {
        url = `${this.configService.apiHost}/${path.replace(/^\/+/, '')}`;
      }
      // Names and addresses are stored data: escaped, so that a "&" or a "<" cannot break the list
      const safeName = this.escapeHtml(name);
      const safeUrl = this.escapeHtml(url);
      body = body.concat(
        `<li>${safeName}: <a href="${safeUrl}">${safeUrl}</a></li><br>`
      );
    });
    body = body.concat(`</ul>`);

    // Take only emails from recipients
    // let recipients = this.selectedRecipients.map(recipient => recipient.email);

    let newCommunication = {
      recipients: this.model.recipients,
      recipientsCc: null,
      recipientsBcc: null,
      attachments: this.model.attachments,
      subject: this.model.subject,
      body,
      sendDocs: true, // tells backend to generate the correct "docs email template"
      companyData: this.data.contract.companyData,
      propertyName: this.data.contract.propertyName,
    };

    if (
      Array.isArray(this.model.recipientsCc) &&
      this.model.recipientsCc.length
    ) {
      newCommunication = {
        ...newCommunication,
        recipientsCc: this.model.recipientsCc,
      };
    } else {
      delete newCommunication.recipientsCc;
    }

    if (
      Array.isArray(this.model.recipientsBcc) &&
      this.model.recipientsBcc.length
    ) {
      newCommunication = {
        ...newCommunication,
        recipientsBcc: this.model.recipientsBcc,
      };
    } else {
      delete newCommunication.recipientsBcc;
    }

    this.subscriptions.push(
      this.communicationsService
        .create(newCommunication)
        .pipe(
          finalize(() => {
            this.formBtnOptions.active = false;
            this.formBtnOptions.disabled = false;
            this.dialogIsLoading = false;
          })
        )
        .subscribe(
          (res) => {
            if (res.status) {
              const data = {
                ...res.data,
                cancellationType: this.model.cancellationType,
                selectedDocs: this.selectedDocs,
              };
              this.toastrService.success(res.message);
              this.communicationsService.onCommunicationCreated();
              this.onClose(data, true);
              return;
            }
            this.toastrService.error(
              this.translateService.instant("common.errors.unknown")
            );
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
    );
  }

  /**
   * Escape a value written into the HTML of the message (a document name or a link)
   *
   * @since 3.0.0
   */
  private escapeHtml(value: unknown): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Update recipients basing on radio buttons changes
   *
   * @since 1.0.0
   */
  onChooseRecipientDialogOpen(key: string): void {
    if (!key || !this.phonebook || !this.phonebook.length) {
      this.toastrService.error(
        this.translateService.instant("common.words.display_no_data")
      );
      return;
    }

    // Open dialog
    const dialogRef = this.chooseRecipientsDialog.open(
      ChooseRecipientDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          phonebook: this.phonebook,
          model: this.model[key],
        },
      }
    );

    // On dialog closing, let's update model
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe(
        (res) => {
          if (res) {
            const phonebookEmails = this.phonebook.map(
              (contact) => contact.email
            );
            const customInsertedEmails = this.model[key]
              ? this.model[key].filter(
                  (email) => !phonebookEmails.includes(email)
                )
              : [];
            const newData = [...res, ...customInsertedEmails];
            this.model = {
              ...this.model,
              [key]: newData,
            };
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }
}
